/**
 * Conversation identity resolution — WP-479 E2 (client half), MCP server side.
 *
 * Deliberate duplicate of packages/cli/src/lib/conversation.ts — the two packages are published
 * independently (no shared workspace dependency between them), so this is a minimal, intentional
 * fork rather than a cross-package import. Keep the two files' resolution logic in sync by hand
 * if the priority order or normalization rule changes.
 *
 * The MCP server is a subprocess of the harness (Claude Code / Cursor / Codex), so the same env
 * channels that a Bash-tool child process would see are visible here too. Resolved once at
 * startup (see startAgentSession / recoverSessionState in ../client.ts) and held for the process
 * lifetime — there is no `--conversation` flag equivalent on this surface, so there is no
 * explicit-override parameter here (unlike the CLI twin).
 *
 * Priority order (harness findings: INS-2122, founder-run tests 2026-07-10):
 *   1. `PB_CONVERSATION_ID` — the universal seam any harness adapter can set.
 *   2. `CLAUDE_CODE_SESSION_ID` — present in Claude Code 2.1.206+ Bash shells (version-dependent,
 *      treat as detected, never asserted).
 *   3. `CURSOR_CONVERSATION_ID` — confirmed live in cursor-agent 2026.07.01 shell env.
 *   4. `CODEX_TUI_SESSION_LOG_PATH` — Codex has no native conversation id; this Superset-injected
 *      log path is unique per session run and stands in as the discriminator. It always contains
 *      `/`, so normalization (below) always hashes it — no special-casing needed here.
 *   5. none of the above → `null` (legacy behavior — no conversation disambiguation).
 */

import { createHash } from "node:crypto";

/** Above this raw length, or containing any char outside the join-key-safe set, hash instead. */
const MAX_RAW_LEN = 128;

/** Join-key-safe charset. */
const SAFE_CHARS_RE = /^[A-Za-z0-9._-]+$/;

/**
 * Normalize a raw candidate conversation id into a value that is always safe to use as a Convex
 * join key. Never truncates (a `slice()` would collide two distinct long ids into one lineage) —
 * oversized or unsafe-charset ids are replaced whole with their sha256 hex digest.
 */
export function normalizeConversationId(raw: string): string {
  if (raw.length > MAX_RAW_LEN || !SAFE_CHARS_RE.test(raw)) {
    return createHash("sha256").update(raw).digest("hex");
  }
  return raw;
}

function pickRawConversationId(): string | null {
  const candidates = [
    process.env.PB_CONVERSATION_ID,
    process.env.CLAUDE_CODE_SESSION_ID,
    process.env.CURSOR_CONVERSATION_ID,
    process.env.CODEX_TUI_SESSION_LOG_PATH,
  ];
  for (const candidate of candidates) {
    if (candidate && candidate.trim().length > 0) return candidate.trim();
  }
  return null;
}

/**
 * Resolve the effective conversation id for this MCP server process. Pure env-based feature
 * detection — with none of the channel env vars set, returns `null` and callers fall back to
 * legacy (no-conversation) behavior.
 */
export function resolveConversationId(): string | null {
  const raw = pickRawConversationId();
  if (!raw) return null;
  return normalizeConversationId(raw);
}
